var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import { VBox } from "../structural_components/hbox_vbox";
import { Label, DescriptionLabel, ErrorLabel } from "./label";
import { createControlId } from "./id_creator";
import { bind } from "./control";
import { createEntityFieldGetter } from "../state/entity";
import { getFieldErrorsAsString } from "../validation/validatableentity";
import { cls_trigger, cls_icon, cls_content, cls_viewport, cls_item, cls_label, cls_separator, cls_item_indicator, cls_scroll_button, cls_error, } from "./select.css";
import { cls_form_item_box } from "./form_item.css";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { useif } from "framework/state/types";
import { cls_error_label_elem, cls_error_tooltip_elem } from "./controls.css";
import { join } from "framework/helpers";
import { Tooltip, TooltipContentBox } from "./tooltip";
import { cls_display_none } from "style/index.css";
var SelectTrigger = function (props) {
    var className = props.className, other_props = __rest(props, ["className"]);
    var cls = join(cls_trigger, className);
    return React.createElement(SelectPrimitive.SelectTrigger, __assign({}, other_props, { className: cls }));
};
var SelectIcon = function (props) {
    return React.createElement(SelectPrimitive.SelectIcon, __assign({}, props, { className: cls_icon }));
};
var StyledContent = React.forwardRef(function (props, forwardRef) {
    var className = props.className, other_props = __rest(props, ["className"]);
    var cls = join(cls_content, className);
    return React.createElement(SelectPrimitive.Content, __assign({ ref: forwardRef }, other_props, { className: cls }));
});
var SelectViewport = function (props) {
    return React.createElement(SelectPrimitive.Viewport, __assign({}, props, { className: cls_viewport }));
};
var SelectContent = function (props) {
    var children = props.children, other_props = __rest(props, ["children"]);
    return (React.createElement(SelectPrimitive.Portal, null,
        React.createElement(StyledContent, __assign({}, other_props), children)));
};
var Item = function (props) {
    return React.createElement(SelectPrimitive.Item, __assign({}, props, { className: cls_item }));
};
var SelectLabel = function (props) {
    return React.createElement(SelectPrimitive.Label, __assign({}, props, { className: cls_label }));
};
var SelectSeparator = function (props) {
    return React.createElement(SelectPrimitive.Separator, __assign({}, props, { className: cls_separator }));
};
var SelectItemIndicator = function (props) {
    return React.createElement(SelectPrimitive.ItemIndicator, __assign({}, props, { className: cls_item_indicator }));
};
var SelectScrollUpButton = function (props) {
    return React.createElement(SelectPrimitive.ScrollUpButton, __assign({}, props, { className: cls_scroll_button }));
};
var SelectScrollDownButton = function (props) {
    return React.createElement(SelectPrimitive.ScrollDownButton, __assign({}, props, { className: cls_scroll_button }));
};
var SelectRadix = SelectPrimitive.Root;
var SelectValue = SelectPrimitive.Value;
var SelectGroup = SelectPrimitive.Group;
var SelectItemText = SelectPrimitive.ItemText;
var SelectBase = function (props) {
    var _a, _b;
    var label = props.label, description = props.description, error = props.error;
    var id = createControlId();
    var binding = bind(props);
    var value = props.value;
    if (binding.value) {
        value = binding.value;
    }
    var onchange = props.onValueChange;
    if (binding.onChange) {
        onchange = binding.onChange;
    }
    var error_tooltip_content = error ? React.createElement(TooltipContentBox, { className: [useif(!props.errorAsTooltip, cls_display_none), cls_error_tooltip_elem] },
        React.createElement(ErrorLabel, null, error)) : null;
    var error_label = error ? React.createElement(ErrorLabel, { className: [useif(props.errorAsTooltip, cls_display_none), cls_error_label_elem] }, error) : null;
    var cls_trigger = [props.className, useif(error, cls_error)].join(' ');
    return React.createElement(VBox, { className: cls_form_item_box },
        label ? React.createElement(Label, null, label) : React.createElement(React.Fragment, null),
        React.createElement(SelectRadix, { onValueChange: function (e) { return onchange(e.valueOf()); }, value: value, disabled: props.disabled },
            React.createElement(Tooltip, { content: error_tooltip_content },
                React.createElement(SelectTrigger, { "aria-label": label, className: cls_trigger },
                    props.keyToTextMapFunc ? React.createElement(SelectValue, { placeholder: (_a = props.placeholder) !== null && _a !== void 0 ? _a : "Zvolte..." }, props.keyToTextMapFunc(value)) : React.createElement(SelectValue, { placeholder: (_b = props.placeholder) !== null && _b !== void 0 ? _b : "Zvolte..." }),
                    React.createElement(SelectIcon, null,
                        React.createElement(ChevronDownIcon, null)))),
            React.createElement(SelectContent, { className: props.contentClassName },
                React.createElement(SelectScrollUpButton, null,
                    React.createElement(ChevronUpIcon, null)),
                React.createElement(SelectViewport, null, props.children),
                React.createElement(SelectScrollDownButton, null,
                    React.createElement(ChevronDownIcon, null)))),
        description ? React.createElement(DescriptionLabel, { htmlFor: id }, description) : React.createElement(React.Fragment, null),
        error_label);
};
export var Select = function (props) {
    var _a, _b;
    var getter = null;
    if (props.getter) {
        getter = props.getter;
    }
    else if (props.state) {
        getter = createEntityFieldGetter(props.state);
    }
    var setter = null;
    if (props.setter) {
        setter = props.setter;
    }
    else if (props.actions) {
        setter = props.actions.setEntityField;
    }
    var error = (_b = (_a = props.error) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : (props.state && props.name ? getFieldErrorsAsString(props.state, props.name) : null);
    var cls = join(props.className);
    var content_cls = join(props.contentClassName);
    return React.createElement(SelectBase, { label: props.label, name: props.name, description: props.description, getter: getter, setter: setter, error: error, keyToTextMapFunc: props.keyToTextMapFunc, value: props.value, onValueChange: props.onValueChange, errorAsTooltip: props.errorAsTooltip, disabled: props.disabled, className: cls, contentClassName: content_cls, placeholder: props.placeholder }, props.children);
};
export var SelectItem = function (props) {
    return React.createElement(Item, { value: props.value },
        React.createElement(SelectItemText, null, props.children),
        React.createElement(SelectItemIndicator, null,
            React.createElement(CheckIcon, null)));
};
